import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { normalizeLocation, type ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import mapObject from "@-/com.npmjs.map-obj";
import memoize from "@-/com.npmjs.memoize";
import { createStaticTableQueries } from "#create-static-table-queries";
import type { StaticQueries, StaticQueryTable, StaticTableQueries } from "#types";

// Shim so each call returns the precise per-table queries; without it the widened
// `StaticQueryTable` collapses the factory's config params to `never`.
/* eslint-disable typescript/no-unsafe-type-assertion -- intentional signature shim */
const createStaticQueriesForTable = createStaticTableQueries as <TTable extends StaticQueryTable>(
  location: ImportMetaLocation,
  table: TTable,
) => StaticTableQueries<TTable>;
/* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */

/**
 * The per-table static query map, bound to the caller's `import.meta.location`
 * so query logs are attributed to the read's origin. Call as
 * `staticQueries(import.meta.location).<table>.<findFirst|findMany>.…`; pass
 * `null` to opt out of logging (tests).
 *
 * Memoized on the location: `import.meta.location` is a fresh object literal per
 * evaluation, so the map is rebuilt once per distinct callsite rather than every
 * render.
 */
export const staticQueries: StaticQueries = memoize(
  (location: ImportMetaLocation) =>
    // `mapObject` yields a uniform value type; the built map is a per-table mapped type.
    /* eslint-disable typescript/no-unsafe-type-assertion -- per-table mapped shape */
    mapObject(staticSchema, (table) => [
      table,
      createStaticQueriesForTable(location, table),
    ]) as unknown as ReturnType<StaticQueries>,
  /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */ {
    cacheKey: ([location]) => normalizeLocation(location),
  },
);
